import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  Loader2,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Ban,
  Minus,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Badge Variants ───────────────────────────────────────────────────────── */

const badgeVariants = cva(
  'inline-flex items-center border text-xs font-medium px-2.5 py-0.5 rounded-full transition-colors',
  {
    variants: {
      variant: {
        default:
          'bg-raios-elevated/15 text-raios-text border-raios-elevated/30',
        brand:
          'bg-raios-accent/15 text-raios-accent-text border-raios-accent/30',
        success:
          'bg-raios-success/15 text-raios-success-text border-raios-success/30',
        warning:
          'bg-raios-warning/15 text-raios-warning-text border-raios-warning/30',
        danger:
          'bg-raios-danger/15 text-raios-danger-text border-raios-danger/30',
        info:
          'bg-raios-info/15 text-raios-info-text border-raios-info/30',
        outline:
          'bg-transparent text-raios-text border-raios-border',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ variant, className }))}
        {...props}
      />
    );
  }
);

Badge.displayName = 'Badge';

/* ─── StatusBadge ──────────────────────────────────────────────────────────── */

type StatusType =
  | 'running'
  | 'pending'
  | 'success'
  | 'error'
  | 'warning'
  | 'cancelled'
  | 'idle';

interface StatusConfig {
  icon: LucideIcon;
  bg: string;
  text: string;
  iconClassName?: string;
}

const statusMap: Record<StatusType, StatusConfig> = {
  running: {
    icon: Loader2,
    bg: 'bg-raios-accent/15',
    text: 'text-raios-accent-text',
    iconClassName: 'animate-spin',
  },
  pending: {
    icon: Clock,
    bg: 'bg-raios-warning/15',
    text: 'text-raios-warning-text',
  },
  success: {
    icon: CheckCircle2,
    bg: 'bg-raios-success/15',
    text: 'text-raios-success-text',
  },
  error: {
    icon: XCircle,
    bg: 'bg-raios-danger/15',
    text: 'text-raios-danger-text',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-raios-warning/15',
    text: 'text-raios-warning-text',
  },
  cancelled: {
    icon: Ban,
    bg: 'bg-raios-elevated/15',
    text: 'text-raios-muted',
  },
  idle: {
    icon: Minus,
    bg: 'bg-raios-elevated/15',
    text: 'text-raios-muted',
  },
};

interface StatusBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  status: StatusType;
  label?: string;
}

const StatusBadge = React.forwardRef<HTMLDivElement, StatusBadgeProps>(
  ({ status, label, className, ...props }, ref) => {
    const config = statusMap[status] ?? statusMap.idle;
    const Icon = config.icon;
    const displayLabel = label ?? status;

    return (
      <div
        ref={ref}
        role="status"
        aria-label={`Status: ${displayLabel}`}
        className={cn(
          'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-0.5 rounded-full',
          config.bg,
          config.text,
          className
        )}
        {...props}
      >
        <Icon className={cn('h-3.5 w-3.5', config.iconClassName)} aria-hidden="true" />
        {displayLabel}
      </div>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants, StatusBadge };
export type { BadgeProps, StatusBadgeProps, StatusType };
